import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError, fileUrl } from "@/lib/apiClient";
import { toast } from "sonner";
import { ClipboardCheck, Camera, Image as ImageIcon, Loader2, X } from "lucide-react";
import { SERVICE_TYPES } from "@/lib/status";

export const SURVEY_FIELDS = [
  ["evaporator_location", "Local da unidade interna (evaporadora)"],
  ["condenser_location", "Local da unidade externa (condensadora)"],
  ["piping_distance", "Distância da tubulação"],
  ["electrical", "Parte elétrica (ponto, disjuntor, tensão)"],
  ["support", "Suporte / fixação"],
  ["drain", "Dreno"],
  ["materials", "Materiais necessários"],
  ["observations", "Observações"],
];

function SurveyPhotos({ photos, onChange, disabled }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (photos.length >= 10) { toast.error("Máximo de 10 fotos."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange([...photos, data.path]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha no upload");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="survey-photos">
      {photos.map((p, i) => (
        <div key={p} className="relative h-14 w-14 rounded-md overflow-hidden border border-slate-200">
          <img src={fileUrl(p)} alt="Vistoria" className="h-full w-full object-cover" data-testid={`survey-photo-${i}`} />
          {!disabled && (
            <button type="button" className="absolute top-0 right-0 bg-white/90 rounded-bl p-0.5" onClick={() => onChange(photos.filter((_, j) => j !== i))}
              data-testid={`survey-photo-remove-${i}`}><X className="h-3 w-3 text-red-500" /></button>
          )}
        </div>
      ))}
      {uploading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      {!disabled && (
        <>
          <label className="cursor-pointer inline-flex items-center gap-1 rounded-md border border-slate-200 text-xs px-2.5 py-1.5 hover:bg-slate-50">
            <Camera className="h-3.5 w-3.5" /> Tirar foto
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid="survey-photo-camera-input" />
          </label>
          <label className="cursor-pointer inline-flex items-center gap-1 rounded-md border border-slate-200 text-xs px-2.5 py-1.5 hover:bg-slate-50">
            <ImageIcon className="h-3.5 w-3.5" /> Galeria
            <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="survey-photo-input" />
          </label>
        </>
      )}
    </div>
  );
}

export default function OsSurveySection({ v, set, disabled, canEditDates }) {
  const sv = v.survey || {};
  const setSv = (k, val) => set("survey", { ...sv, [k]: val });
  const types = v.serviceType && !SERVICE_TYPES.includes(v.serviceType) ? [v.serviceType, ...SERVICE_TYPES] : SERVICE_TYPES;
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="os-survey-section">
      <Label className="text-sm flex items-center gap-1.5"><ClipboardCheck className="h-4 w-4 text-[#1e7be0]" /> Tipo de serviço, orçamento e vistoria</Label>
      <div className="grid sm:grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label className="text-xs">Tipo de serviço da OS</Label>
          <Select value={v.serviceType || ""} disabled={disabled} onValueChange={(x) => set("serviceType", x)}>
            <SelectTrigger data-testid="os-service-type-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {types.map((t) => <SelectItem key={t} value={t} data-testid={`os-service-type-${t}`}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Orçamento</Label>
          <Select value={v.budget || "none"} disabled={disabled} onValueChange={(x) => set("budget", x === "none" ? "" : x)}>
            <SelectTrigger data-testid="os-budget-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Sem definição</SelectItem>
              <SelectItem value="aprovado" data-testid="os-budget-aprovado">Aprovado</SelectItem>
              <SelectItem value="nao_aprovado" data-testid="os-budget-nao_aprovado">Não aprovado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label className="text-xs">Data real da 1ª visita (avaliação)</Label>
          <div className="flex gap-2">
            <Input type="date" value={v.firstVisitDate} disabled={disabled} onChange={(e) => set("firstVisitDate", e.target.value)} data-testid="os-first-visit-date" />
            <Input type="time" className="w-28" value={v.firstVisitTime} disabled={disabled} onChange={(e) => set("firstVisitTime", e.target.value)} data-testid="os-first-visit-time" />
          </div>
        </div>
        {canEditDates && (
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs">Data de conclusão</Label>
            <div className="flex gap-2">
              <Input type="date" value={v.concludedDate} onChange={(e) => set("concludedDate", e.target.value)} data-testid="os-concluded-date" />
              <Input type="time" className="w-28" value={v.concludedTime} onChange={(e) => set("concludedTime", e.target.value)} data-testid="os-concluded-time" />
            </div>
          </div>
        )}
      </div>
      <div className="space-y-2 rounded-md bg-slate-50 p-2.5">
        <p className="text-xs font-semibold text-[#0a2a6c]">Laudo da vistoria / levantamento (1ª visita)</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {SURVEY_FIELDS.map(([k, l]) => (
            <div key={k} className={`space-y-1 ${["materials", "observations"].includes(k) ? "sm:col-span-2" : ""}`}>
              <Label className="text-[11px] text-slate-600">{l}</Label>
              {["materials", "observations"].includes(k)
                ? <Textarea rows={2} value={sv[k] || ""} disabled={disabled} onChange={(e) => setSv(k, e.target.value)} data-testid={`survey-${k}`} />
                : <Input className="h-9" value={sv[k] || ""} disabled={disabled} onChange={(e) => setSv(k, e.target.value)} data-testid={`survey-${k}`} />}
            </div>
          ))}
        </div>
        <Label className="text-[11px] text-slate-600">Fotos da vistoria</Label>
        <SurveyPhotos photos={sv.photos || []} onChange={(p) => setSv("photos", p)} disabled={disabled} />
      </div>
    </div>
  );
}
